import { useCallback, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { useAPI } from "../../apiv1";
import { Error as APIError } from "../../types/apiv1";
import RecoverAccountUI, { Feedback, Done } from "../ui/RecoverAccountUI";
import * as ActivateAccountScreenUI from "../ui/ActivateAccountScreenUI";
import zxcvbn, { ZXCVBNResult } from "zxcvbn";
import { decode } from "js-base64";
import { useAppDispatch } from "../../store/hooks";
import { login } from "../../store/thunk/auth";

function passwordStrength(password: string): ZXCVBNResult {
	return zxcvbn(password, [
		"mit", "beredskab", "mitberedskab", "mb",
	]);
}

type PendingAccountRecovery = {
	request_id: string,
	token: string,
	email: string,
}

function isPendingAccountRecovery(obj: any): obj is PendingAccountRecovery {
	return (
		obj != null &&
		typeof obj === 'object' &&
		typeof obj['request_id'] === 'string' &&
		typeof obj['token'] === 'string' &&
		typeof obj['email'] === 'string'
	);
}

function RecoverAccount({ pendingAccountRecovery }: { pendingAccountRecovery: PendingAccountRecovery }) {
	const api = useAPI();
	const dispatch = useAppDispatch();
	const [feedback, setFeedback] = useState<Feedback>();
	const [pending, setPending] = useState(false);
	const [password, setPassword] = useState("");
	const strength = passwordStrength(password);
	const [confirmPassword, setConfirmPassword] = useState("");

	const submit = useCallback(async () => {
		setPending(true);
		try {
			await api.auth.reset(pendingAccountRecovery.request_id, pendingAccountRecovery.token, password);
			setFeedback(Feedback.Accepted);
			dispatch(login(pendingAccountRecovery.email, password));
		} catch (e) {
			switch (e) {
			case APIError.Forbidden:
				setFeedback(Feedback.RejectedInvalid);
				break;
			case APIError.Network:
				setFeedback(Feedback.RejectedNetwork);
				break;
			default:
				setFeedback(Feedback.RejectedBug);
			}
		} finally {
			setPending(false);
		}
	}, [api, pendingAccountRecovery, password, setPending, dispatch]);

	const allowSubmit = strength.score > 2 && password === confirmPassword;

	if (feedback === Feedback.RejectedInvalid || feedback === Feedback.Accepted) {
		return <Done feedback={feedback} />
	}

	return <RecoverAccountUI
		pending={pending}
		feedback={feedback}
		passwordStrength={strength.score}
		email={pendingAccountRecovery.email}
		password={password} setPassword={setPassword}
		onSubmit={allowSubmit ? submit : undefined}
		confirmPassword={{
			value: confirmPassword, setValue: setConfirmPassword,
			error: confirmPassword !== "" && password !== confirmPassword
				? ActivateAccountScreenUI.ConfirmPasswordErrors.Mismatch
				: undefined
		}}
	/>;
}

export default function RecoverAccountURLDecoder() {
	const encodedData = useLocation().hash.substring(1);
	
	// JavaScript strings use UTF-8. The base64 data however, is encoded
	// from a UTF-8 string. This casuses window.atob to spit out garbage
	// when using non-ASCII characters. The js-base64 library handles UTF-8
	// correctly, so we are using that instead.
	const pendingAccountRecovery = useMemo(() => {
		try {
			return JSON.parse(decode(encodedData));
		} catch (e) {
			// The JSON is invalid.
			return {};
		}
	}, [encodedData]);

	if (!isPendingAccountRecovery(pendingAccountRecovery)) {
		return <Done feedback={Feedback.RejectedInvalid} />;
	}

	return <RecoverAccount pendingAccountRecovery={pendingAccountRecovery} />;
}
